import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { 
  RocketLaunch, 
  Star, 
  Business, 
  HandshakeOutlined,
  Check,
  Support,
  Analytics,
  Security 
} from '@mui/icons-material';
import OnboardingLayout from '../../components/host/OnboardingLayout';
import { SelectField, NavigationButtons, StyledButton } from '../../components/host/OnboardingComponents';
import styled from 'styled-components';

const PlanCard = styled(Card)<{ selected?: boolean; featured?: boolean }>`
  background: ${props => props.selected ? '#2a3441' : '#1a202c'};
  border: ${props => 
    props.selected ? '2px solid #377dff' : 
    props.featured ? '2px solid #377dff' :
    '1px solid #4a5568'};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: #377dff;
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(55, 125, 255, 0.2);
  }
  
  ${props => props.featured && `
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: linear-gradient(135deg, #377dff, #2563eb);
      border-radius: 16px;
      z-index: -1;
    }
  `}
`;

const FeatureItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #b0b8c1;
  
  & .feature-icon {
    color: #4ade80;
    margin-right: 8px;
    font-size: 18px;
  }
  
  & .feature-text {
    font-size: 14px;
  }
`;

interface SubscriptionData {
  selectedPlan: string;
  securitySubscription: string;
  quantity: number;
  totalCost: string | number;
}

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: 29,
    icon: <RocketLaunch />,
    features: [
      'Up To 5 Botels',
      'Basic Analytics',
      'Email Support'
    ],
    color: '#6b7280'
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 49,
    icon: <Star />,
    featured: true,
    features: [
      'Up To 15 Botels',
      'Advanced Analytics',
      '24/7 Support'
    ],
    color: '#377dff'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 79,
    icon: <Business />,
    features: [
      'Up to 50 Botels',
      'Custom Analytics',
      'Priority Support'
    ],
    color: '#059669'
  },
  {
    id: 'partner',
    name: 'Large-Scale Partner',
    price: 'custom',
    icon: <HandshakeOutlined />,
    features: [
      'Unlimited Botels',
      'White Label Solution',
      'Dedicated Account Manager'
    ],
    color: '#7c3aed'
  }
];

const securityOptions = [
  { value: 'basic_adt', label: 'Basic ADT' },
  { value: 'premium_adt', label: 'Premium ADT' },
  { value: 'enterprise_adt', label: 'Enterprise ADT' },
  { value: 'custom_security', label: 'Custom Security Package' }
];

const HostOnboardingStep3 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SubscriptionData>({
    selectedPlan: 'premium',
    securitySubscription: 'basic_adt',
    quantity: 1,
    totalCost: 49.00,
  });

  const handleNext = () => {
    if (canProceed()) {
      localStorage.setItem('hostOnboardingStep3', JSON.stringify(formData));
      navigate('/host/onboarding/step-4');
    }
  };

  const handlePrevious = () => {
    navigate('/host/onboarding/step-2');
  };

  const canProceed = () => {
    return formData.selectedPlan && formData.securitySubscription && formData.quantity > 0;
  };

  const calculateTotal = () => {
    const selectedPlanData = plans.find(plan => plan.id === formData.selectedPlan);
    if (!selectedPlanData || selectedPlanData.price === 'custom') {
      return 'Custom Pricing';
    }
    return ((selectedPlanData.price as number) * formData.quantity).toFixed(2);
  };

  // Update totalCost whenever selectedPlan or quantity changes
  useEffect(() => {
    const selectedPlanData = plans.find(plan => plan.id === formData.selectedPlan);
    if (!selectedPlanData || selectedPlanData.price === 'custom') {
      setFormData(prev => ({ ...prev, totalCost: 'Custom Pricing' }));
    } else {
      const calculatedTotal = parseFloat(((selectedPlanData.price as number) * formData.quantity).toFixed(2));
      setFormData(prev => ({ ...prev, totalCost: calculatedTotal }));
    }
  }, [formData.selectedPlan, formData.quantity]);

  const updateFormData = (field: keyof SubscriptionData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <OnboardingLayout
      currentStep={3}
      totalSteps={4}
      stepTitle="Choose Your Subscription Plan"
      stepSubtitle="Select the perfect plan for your Botel management needs"
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Plan Selection */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {plans.map((plan) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={plan.id}>
              <PlanCard
                selected={formData.selectedPlan === plan.id}
                featured={plan.featured}
                onClick={() => updateFormData('selectedPlan', plan.id)}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  {plan.featured && (
                    <Chip
                      label="RECOMMENDED"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#377dff',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  
                  <Box sx={{ color: plan.color, mb: 2, fontSize: '2rem' }}>
                    {plan.icon}
                  </Box>
                  
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                    {plan.name}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {plan.price === 'custom' ? (
                      <Typography variant="h4" sx={{ color: '#377dff', fontWeight: 700 }}>
                        custom
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="h4" sx={{ color: '#377dff', fontWeight: 700 }}>
                          ${plan.price}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                          /botel/month
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      /pricing
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'left' }}>
                    {plan.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <Check className="feature-icon" />
                        <span className="feature-text">{feature}</span>
                      </FeatureItem>
                    ))}
                  </Box>
                </CardContent>
              </PlanCard>
            </Grid>
          ))}
        </Grid>

        {/* Subscription Calculator */}
        <Box
          sx={{
            background: '#2a3441',
            border: '1px solid #3a4a5c',
            borderRadius: 3,
            p: 4,
            mb: 4
          }}
        >
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 3, textAlign: 'center' }}>
            Calculate Your Subscription
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectField
                label="Select Plan"
                value={formData.selectedPlan}
                onChange={(value) => updateFormData('selectedPlan', value)}
                options={plans.map(plan => ({ 
                  value: plan.id, 
                  label: `${plan.name} Plan` 
                }))}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectField
                label="Security Subscription"
                value={formData.securitySubscription}
                onChange={(value) => updateFormData('securitySubscription', value)}
                options={securityOptions}
                required
              />   
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectField
                label="Select Botels Qty"
                value={formData.quantity.toString()}
                onChange={(value) => updateFormData('quantity', parseInt(value))}
                options={Array.from({ length: 10 }, (_, i) => ({
                  value: (i + 1).toString(),
                  label: (i + 1).toString()
                }))}
                required
              />
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" sx={{ color: '#b0b8c1', mb: 1 }}>
              Total Cost
            </Typography>
            <Typography variant="h3" sx={{ color: '#377dff', fontWeight: 700 }}>
              ${typeof formData.totalCost === 'string' ? formData.totalCost : formData.totalCost.toFixed(2)}{typeof formData.totalCost === 'string' ? '' : ' /per month'}
            </Typography>
            {formData.selectedPlan === 'partner' && (
              <Typography variant="body2" sx={{ color: '#b0b8c1', mt: 1 }}>
                Contact our sales team for custom pricing
              </Typography>
            )}
          </Box>
        </Box>

        {/* Additional Information */}
        {/* <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Security sx={{ color: '#377dff', fontSize: '2rem', mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                Security Included
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                All plans include basic security monitoring
              </Typography>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Analytics sx={{ color: '#377dff', fontSize: '2rem', mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                Analytics Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                Track performance and earnings in real-time
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Support sx={{ color: '#377dff', fontSize: '2rem', mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                24/7 Support
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                Get help whenever you need it
              </Typography>
            </Box>
          </Grid>
        </Grid> */}

        {/* Continue Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <StyledButton
            variant="primary"
            size="large"
            onClick={handleNext}
            disabled={!canProceed()}
            sx={{ 
              px: 6, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'white',
              backgroundColor: '#377dff',
              borderRadius: 2,
            }}
          >
            Continue To Order & Payment →
          </StyledButton>
        </Box>

        {/* Navigation */}
        <NavigationButtons
          onPrevious={handlePrevious}
          previousLabel="Back"
        />
      </Box>
    </OnboardingLayout>
  );
};

export default HostOnboardingStep3;
